import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// EN mirror of /translate — certified translation hub (50+ languages)
import { createFileRoute, Link } from "@tanstack/react-router";
import { speakableSchema, howToSchema } from "@/lib/aeo";
import { Languages, BadgeCheck, Clock3 } from "lucide-react";
import { TranslateVisaCrossLink } from "@/components/TranslateVisaCrossLink";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import HubCountryLinks from "@/components/HubCountryLinks";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_EN } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Certified Translation Bangkok — 50+ Languages | NYC";
const DESC =
  "Certified translation in Bangkok accepted by MFA, embassies, courts & immigration. 50+ languages. Send your documents for a free eligibility check.";

const FEATURED = [
  { slug: "english", nameEn: "English", from: 250, flag: "🇬🇧" },
  { slug: "japanese", nameEn: "Japanese", from: 400, flag: "🇯🇵" },
  { slug: "chinese", nameEn: "Chinese (Simplified)", from: 350, flag: "🇨🇳" },
  { slug: "korean", nameEn: "Korean", from: 450, flag: "🇰🇷" },
  { slug: "german", nameEn: "German", from: 500, flag: "🇩🇪" },
  { slug: "french", nameEn: "French", from: 500, flag: "🇫🇷" },
  { slug: "spanish", nameEn: "Spanish", from: 500, flag: "🇪🇸" },
  { slug: "italian", nameEn: "Italian", from: 550, flag: "🇮🇹" },
  { slug: "russian", nameEn: "Russian", from: 550, flag: "🇷🇺" },
  { slug: "arabic", nameEn: "Arabic", from: 600, flag: "🇸🇦" },
  { slug: "vietnamese", nameEn: "Vietnamese", from: 400, flag: "🇻🇳" },
  { slug: "indonesian", nameEn: "Indonesian (Bahasa)", from: 450, flag: "🇮🇩" },
  { slug: "dutch", nameEn: "Dutch", from: 600, flag: "🇳🇱" },
  { slug: "portuguese", nameEn: "Portuguese", from: 550, flag: "🇵🇹" },
  { slug: "swedish", nameEn: "Swedish", from: 650, flag: "🇸🇪" },
  { slug: "polish", nameEn: "Polish", from: 600, flag: "🇵🇱" },
  { slug: "turkish", nameEn: "Turkish", from: 550, flag: "🇹🇷" },
  { slug: "hindi", nameEn: "Hindi", from: 500, flag: "🇮🇳" },
  { slug: "danish", nameEn: "Danish", from: 650, flag: "🇩🇰" },
  { slug: "norwegian", nameEn: "Norwegian", from: 650, flag: "🇳🇴" },
];

export const Route = createFileRoute("/en/translate/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("translate", "en", "https://nyclanguageinstitute.com/en/translate") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/translate", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/translate", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/translate", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/translate", "en")},
      { property: "og:url", content: `${SITE}/en/translate` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/translate` },
      ...hreflangLinks("/translate", "/en/translate"),
    ],
    scripts: [speakableSchema(),
      howToSchema({
        name: "How to order certified translation in Bangkok",
        description: "Step-by-step guide to ordering MFA-accepted certified translation from NYC Language Institute — accepted by Thai MFA, embassies, courts, and immigration.",
        inLanguage: "en", totalTime: "PT2D", estimatedCost: { currency: "THB", value: "250" },
        steps: [
          { name: "Send document photos via LINE @nycli", text: "Scan or photograph your originals clearly. Send to LINE @nycli with the target language and purpose (visa / marriage / education).", url: `${SITE}/en/quote` },
          { name: "Receive quote + pay 50% deposit", text: "Our team counts pages + urgency within 30 minutes. From THB 250/page, certified by DBD-registered company since 2010." },
          { name: "Translation by MoJ-listed sworn translator", text: "1-3 business days (24h rush available). Every page bears company stamp + sworn translator signature." },
          { name: "Receive PDF + originals", text: "PDF by email + hard copies via EMS nationwide — ready to submit to MFA / embassy directly.", url: `${SITE}/en/apostille` },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Certified Translation Services",
          provider: { "@type": "LegalService", name: "NYC Legal & Notary Service Co., Ltd.", url: SITE },
          areaServed: "TH",
          inLanguage: "en",
          serviceType: "Certified & Sworn Translation",
          offers: { "@type": "Offer", priceCurrency: "THB", price: "250", description: "Per A4 page (≤250 words)" },
        }),
      },
      entitySameAsSchema(),
      softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing certified translation tooling for 50+ languages." }),
      claimReviewSchema(COMMON_CLAIM_REBUTTALS_EN(`${SITE}/en/translate`)),
    
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: EnTranslateHub,
});

function EnTranslateHub() {
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <AIAnswerBox
          question="What certified translation does NYC offer in Bangkok?"
          answer="NYC Language Institute provides certified translation in 50+ languages accepted by Thai MFA, foreign embassies, courts and immigration on first submission — DBD-registered since 2010, MoJ sworn translators and NAATI option, from THB 1,300/page. Standard 24–48h, rush 4–8h."
          stats={[
            { label: "Languages", value: "50+" },
            { label: "From", value: "THB 250/pg" },
            { label: "Standard", value: "24–48h" },
            { label: "DBD", value: "0435567000061" },
          ]}
          sourceUrl={`${SITE}/en/translate`}
          lang="en"
        />
      </div>
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Certified translation — <span className="text-amber-300">50+ languages</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            DBD-registered translation company (0435567000061) since 2010. Every page bears the company stamp and
            sworn translator signature — accepted by Thai MFA, foreign embassies, courts, and immigration on first
            submission.
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Language Institute — Certified Translation Bangkok">
              NYC Language Institute is a DBD-registered translation company (0435567000061) operating in Bangkok since 2010, offering certified translation across 50+ languages — including English, Japanese, Chinese, Korean, German, French, Arabic, Russian, plus rare ASEAN and EU languages. Every page is signed and stamped by a Ministry of Justice sworn translator (with NAATI option for Australia and New Zealand), accepted by Thai MFA, foreign embassies, courts, and immigration on first submission. Standard turnaround 24-48 hours; rush 4-8 hours available.
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "Languages", v: "50+" },
              { k: "Starting price", v: "THB 250 / page" },
              { k: "Standard turnaround", v: "24-48 hours" },
              { k: "Rush option", v: "4-8 hours" },
              { k: "Certifications", v: "MoJ · NAATI · MFA" },
              { k: "DBD registration", v: "0435567000061" },
            ]} />
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-3">
          {[
            { icon: BadgeCheck, t: "Sworn translators", d: "Registered with the Ministry of Foreign Affairs. NAATI option for Australia." },
            { icon: Clock3, t: "Standard 24–48h", d: "Rush 4–8h available. 5,400+ pages delivered per year." },
            { icon: Languages, t: "Rare languages", d: "Hebrew, Tagalog, Burmese, Khmer, Lao — full coverage of ASEAN + EU." },
          ].map((c) => (
            <article key={c.t} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <c.icon className="h-7 w-7 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep">{c.t}</h2>
              <p className="mt-2 text-sm text-slate-600">{c.d}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="px-4 pb-4 pt-2">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold text-navy-deep sm:text-2xl">Certification chains</h2>
          <p className="mt-1 text-sm text-slate-600">Pick the stamp your destination authority demands.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Link to="/en/translate/naati" className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 transition hover:border-emerald-400 hover:shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">NAATI Certified</div>
              <div className="mt-1 font-display text-base font-extrabold text-navy-deep">NAATI for Australia / NZ</div>
              <div className="mt-1 text-xs text-slate-600">DHA · DFAT · INZ · BDM · NZQA</div>
            </Link>
            <Link to="/en/translate/mfa" className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 transition hover:border-amber-400 hover:shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">MFA Legalized</div>
              <div className="mt-1 font-display text-base font-extrabold text-navy-deep">Thai MFA legalization</div>
              <div className="mt-1 text-xs text-slate-600">Chaeng Wattana · 2–5 days</div>
            </Link>
            <Link to="/en/translate/embassy-legalization" className="rounded-xl border border-sky-200 bg-sky-50/40 p-4 transition hover:border-sky-400 hover:shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700">Embassy Legalized</div>
              <div className="mt-1 font-display text-base font-extrabold text-navy-deep">Destination embassy chain</div>
              <div className="mt-1 text-xs text-slate-600">CN · UAE · SA · MM · RU · EG</div>
            </Link>
            <Link to="/en/translate/government" className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 transition hover:border-indigo-400 hover:shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">Government Docs</div>
              <div className="mt-1 font-display text-base font-extrabold text-navy-deep">Thai government records</div>
              <div className="mt-1 text-xs text-slate-600">House book · Birth · DBD · From ฿500/page</div>
            </Link>
            <Link to="/en/translate/visa-docs" className="rounded-xl border border-rose-200 bg-rose-50/40 p-4 transition hover:border-rose-400 hover:shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700">Visa Pack</div>
              <div className="mt-1 font-display text-base font-extrabold text-navy-deep">Full visa document pack</div>
              <div className="mt-1 text-xs text-slate-600">Bank · Payslip · house book · pack ฿4,500</div>
            </Link>
            <Link to="/en/translate/naati/online" className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 transition hover:border-teal-400 hover:shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-widest text-teal-700">NAATI Online</div>
              <div className="mt-1 font-display text-base font-extrabold text-navy-deep">NAATI online · 50 countries</div>
              <div className="mt-1 text-xs text-slate-600">PDF + DHL worldwide</div>
            </Link>
          </div>
        </div>
      </section>



      <section className="bg-slate-50 px-4 py-14">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold text-navy-deep">Most requested languages</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {FEATURED.map((l) => (
              <Link key={l.slug} to="/translate/$language" params={{ language: l.slug }}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm hover:border-amber-400">
                <span className="text-xl">{l.flag}</span>
                <span className="flex-1 font-medium text-navy-deep">{l.nameEn}</span>
                <span className="text-xs text-amber-700">฿{l.from}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-14">
        <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-700">
          Need a translation paired with notarisation or apostille? Bundle at{" "}
          <Link to="/en/apostille" className="font-bold text-amber-700 hover:underline">/en/apostille</Link>{" "}
          or request a written quote at <Link to="/en/quote" className="font-bold text-amber-700 hover:underline">/en/quote</Link>.
        </div>
      </section>
      <TranslateVisaCrossLink mode="from-translate" locale="en" />
      <RelatedComparisons category="translate" locale="en" />
      <HubCountryLinks service="apostille" lang="en" />
          <ServicePageEnhancer serviceName="Translation" locale="en" />

      <PlanningToolsLinks locale="en" />
      </main>
  );
}
